import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  type BlogDraft,
  type BlogParamField,
  type BlogParamGroup,
  type BlogParamValue,
  type GenerateFromBlogParamsResponse,
  type GenerateFromParamsRequest,
  type GenerateFromParamsResponse,
  type MediaResponse,
  type ParameterField,
  type ParameterGroup,
  type ParameterValue,
  type NewsStory,
  type SelectedBlogParam,
  type SelectedParam,
  resolveMediaUrl,
  useBlogParameters,
  useIndustryNews,
  useGenerateBlogDraft,
  useGenerateCarousel,
  useGenerateFromBlogParams,
  useGenerateFromParams,
  useGenerateImage,
  useGenerateReel,
  useReelJob,
  useVisualParameters,
} from '../api/marketing';
import { SiteLayout } from '../components/SiteLayout';
import { colors } from '../theme';
import { FEATURE_LIFESTYLE_CATEGORY } from '../config/flags';

// ── Types ────────────────────────────────────────────────────────────────────

type SelectionEntry = SelectedParam & { pct: number; fieldLabel: string };
type SelectionMap = Record<string, SelectionEntry>; // key = `${group_id}.${field_id}`

// ── Helpers ──────────────────────────────────────────────────────────────────

const GLOBAL_MAX_ENG = 14; // approximate ceiling for opacity normalisation

function strengthLabel(score: number): string {
  if (score >= 51) return 'Dominant signal';
  if (score >= 36) return 'Proven territory';
  if (score >= 21) return 'Tested ground';
  return 'Niche pick';
}

function buildSuggestedPattern(groups: ParameterGroup[]): SelectionMap {
  const result: SelectionMap = {};
  for (const group of groups) {
    for (const field of group.fields) {
      if (!field.available || field.values.length === 0) continue;
      const best = field.values.reduce((a, b) => (a.avg_eng > b.avg_eng ? a : b));
      result[`${group.group_id}.${field.field_id}`] = {
        group_id: group.group_id,
        field_id: field.field_id,
        value_id: best.id,
        value_label: best.label,
        pct: best.pct,
        fieldLabel: field.field_label,
      };
    }
  }
  return result;
}

const GROUP_ORDER = ['human', 'framing', 'environment', 'color', 'product'];

// ── Per-category color coding ─────────────────────────────────────────────────
// Each visual-data group gets its own hue so the eye can tell sections apart at
// a glance. This mirrors the Depletions dashboard's numbered-stage treatment
// (DepletionsDashboardGuide.tsx): the category color lives in thin accents — a
// 3px left rail, the header dot/label, a whisper-faint card tint — while the
// cells themselves stay clean on a light ground. Hues and step order match
// Depletions exactly (indigo → blue → green → amber → violet).
// Within a group the encoding is unchanged: cell height = post frequency,
// fill opacity = engagement lift — expressed as a restrained tint of the
// group's color rather than a saturated flood.
type GroupPalette = { hue: string; hueDark: string; rgb: string };

const GROUP_PALETTES: Record<string, GroupPalette> = {
  human: { hue: '#4f46e5', hueDark: '#3730a3', rgb: '79,70,229' }, // indigo — step 1
  framing: { hue: '#3b82f6', hueDark: '#1d4ed8', rgb: '59,130,246' }, // blue — step 2
  environment: { hue: '#059669', hueDark: '#047857', rgb: '5,150,105' }, // green — step 3
  color: { hue: '#f59e0b', hueDark: '#b45309', rgb: '245,158,11' }, // amber — step 4
  product: { hue: '#8b5cf6', hueDark: '#6d28d9', rgb: '139,92,246' }, // violet — step 5
};

// Fallback for any group id not in the map (keeps the brand gold).
const DEFAULT_PALETTE: GroupPalette = { hue: '#bb8c3f', hueDark: '#8a6524', rgb: '212,168,75' };

function groupPalette(groupId: string): GroupPalette {
  return GROUP_PALETTES[groupId] ?? DEFAULT_PALETTE;
}

// ── HeatCell ──────────────────────────────────────────────────────────────────

function HeatCell({
  val,
  field,
  palette,
  selected,
  suggested,
  mounted,
  onToggle,
}: {
  val: ParameterValue;
  field: ParameterField;
  palette: GroupPalette;
  selected: boolean;
  suggested: boolean;
  mounted: boolean;
  onToggle: () => void;
}) {
  // Restrained tint, Depletions-style — the fill is a whisper of the group
  // color (never a saturated flood). Engagement lift modulates a narrow
  // opacity band so brightness still reads without shouting.
  const engOpacity = field.available ? 0.05 + (val.avg_eng / GLOBAL_MAX_ENG) * 0.16 : 0.03;
  const fillColor = `rgba(${palette.rgb},${engOpacity.toFixed(2)})`;
  const fillH = mounted && field.available ? `${val.pct}%` : '0%';

  const borderSx = selected
    ? { border: `1.5px solid ${palette.hue}` }
    : suggested
      ? { border: `1.5px dashed rgba(${palette.rgb},.5)` }
      : { border: `1.5px solid ${colors.border}` };

  return (
    <Tooltip
      title={
        field.available
          ? `${val.pct}% of top posts · avg ♦${val.avg_eng.toFixed(1)}`
          : 'No data yet — run content analysis'
      }
      placement="top"
      arrow
    >
      <Box
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        aria-label={`${val.label}${field.available ? `: ${val.pct}% of posts` : ''}`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        sx={{
          ...borderSx,
          position: 'relative',
          flex: 1,
          minWidth: 76,
          maxWidth: 148,
          height: 54,
          borderRadius: '6px',
          bgcolor: 'background.paper',
          cursor: 'pointer',
          overflow: 'hidden',
          transition: 'border .15s, transform .12s, box-shadow .15s',
          outline: 'none',
          '&:hover': {
            border: `1.5px solid rgba(${palette.rgb},.6)`,
            transform: 'translateY(-1px)',
            boxShadow: `0 3px 10px rgba(${palette.rgb},.1)`,
          },
          '&:focus-visible': {
            outline: `2px solid ${palette.hue}`,
            outlineOffset: 2,
          },
        }}
      >
        {/* Liquid fill: height = frequency, opacity = engagement intensity */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: fillH,
            bgcolor: selected ? `rgba(${palette.rgb},.12)` : fillColor,
            transition: 'height .72s cubic-bezier(.22,1,.36,1), background-color .25s',
          }}
        />

        {/* Suggested star (hidden once selected) */}
        {suggested && !selected && (
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              top: 3,
              right: 5,
              fontSize: 8,
              color: palette.hue,
              opacity: 0.7,
              zIndex: 2,
              lineHeight: 1,
            }}
          >
            ★
          </Box>
        )}

        {/* Selection checkmark badge */}
        {selected && (
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              top: 4,
              right: 5,
              width: 13,
              height: 13,
              borderRadius: '50%',
              bgcolor: palette.hue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
            }}
          >
            <svg width="7" height="6" viewBox="0 0 7 6" fill="none">
              <path
                d="M1 3l2 2 3-3"
                stroke="#fff"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
        )}

        {/* Label + stats */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: '6px 8px 5px',
          }}
        >
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: selected ? 700 : 600,
              color: selected ? palette.hueDark : 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2,
              pr: selected || suggested ? '16px' : '4px',
              transition: 'color .15s, font-weight .15s',
            }}
          >
            {val.label}
          </Typography>
          {field.available && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography
                component="span"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 9,
                  color: selected ? palette.hueDark : 'text.secondary',
                  fontWeight: selected ? 700 : 500,
                  transition: 'color .15s',
                }}
              >
                {val.pct}%
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 9,
                  color: selected ? palette.hue : 'text.disabled',
                  transition: 'color .15s',
                }}
              >
                ♦{val.avg_eng.toFixed(1)}
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
}

// ── HeatRow ───────────────────────────────────────────────────────────────────

function HeatRow({
  group,
  field,
  selections,
  mounted,
  onToggle,
}: {
  group: ParameterGroup;
  field: ParameterField;
  selections: SelectionMap;
  mounted: boolean;
  onToggle: (entry: SelectionEntry) => void;
}) {
  const key = `${group.group_id}.${field.field_id}`;
  const selEntry = selections[key];
  const palette = groupPalette(group.group_id);
  const sugId =
    field.available && field.values.length > 0
      ? field.values.reduce((a, b) => (a.avg_eng > b.avg_eng ? a : b)).id
      : null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '124px 1fr',
        alignItems: 'flex-start',
        px: 2,
        py: '9px',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: selEntry ? 'text.primary' : 'text.secondary',
          pt: '4px',
          pr: 1.5,
          lineHeight: 1.3,
          transition: 'color .15s',
        }}
      >
        {field.field_label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
        {field.values.map((val) => (
          <HeatCell
            key={val.id}
            val={val}
            field={field}
            palette={palette}
            selected={selEntry?.value_id === val.id}
            suggested={sugId === val.id}
            mounted={mounted}
            onToggle={() =>
              onToggle({
                group_id: group.group_id,
                field_id: field.field_id,
                value_id: val.id,
                value_label: val.label,
                pct: val.pct,
                fieldLabel: field.field_label,
              })
            }
          />
        ))}
      </Box>
    </Box>
  );
}

// ── BarrelGauge ───────────────────────────────────────────────────────────────

function BarrelGauge({ score, selCount }: { score: number | null; selCount: number }) {
  const barrelPct = score !== null ? Math.min(100, score * 1.6) : 0;

  return (
    <Box sx={{ p: 2.5, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      {/* Barrel fill meter */}
      <Box
        sx={{
          flexShrink: 0,
          width: 30,
          height: 88,
          bgcolor: '#f3f4f6',
          border: `1.5px solid ${colors.border}`,
          borderRadius: '4px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Tick lines */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'repeating-linear-gradient(to top, transparent, transparent 21px, rgba(255,255,255,.55) 21px, rgba(255,255,255,.55) 22px)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
        {/* Fill */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: `${barrelPct}%`,
            background: `linear-gradient(to top, ${colors.goldDark}, ${colors.gold}, ${colors.goldLight})`,
            transition: 'height .9s cubic-bezier(.22,1,.36,1)',
            borderRadius: '0 0 2px 2px',
          }}
        />
      </Box>

      {/* Score text */}
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            color: 'text.disabled',
            mb: 0.5,
          }}
        >
          Popularity index
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Palatino Linotype", Palatino, "Book Antiqua", Georgia, serif',
            fontSize: 46,
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: '-.02em',
            color: score !== null ? colors.goldDark : '#d1d5db',
            transition: 'color .4s',
          }}
        >
          {score !== null ? `${score}%` : '—'}
        </Typography>
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            mt: 0.5,
            color: score !== null ? colors.goldDark : 'text.disabled',
            transition: 'color .4s',
          }}
        >
          {score !== null ? strengthLabel(score) : 'No selections'}
        </Typography>
        <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 1, fontFamily: 'monospace' }}>
          {selCount} of 14 dimensions
        </Typography>
      </Box>
    </Box>
  );
}

// ── BriefPanel ────────────────────────────────────────────────────────────────

function BriefPanel({ result }: { result: GenerateFromParamsResponse }) {
  const SKIP = new Set(['_model', '_generated_by']);
  const sections = Object.entries(result.brief)
    .filter(([k, v]) => !SKIP.has(k) && v !== null && v !== undefined)
    .map(([key, val]) => ({
      key,
      label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      text: typeof val === 'string' ? val : JSON.stringify(val, null, 2),
    }));

  return (
    <Box
      sx={{
        mt: 3,
        border: `1.5px solid ${colors.border}`,
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        animation: 'brief-in .35s ease',
        '@keyframes brief-in': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        sx={{
          bgcolor: colors.gold,
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            color: '#1a1005',
          }}
        >
          Generated Brief
        </Typography>
        <Typography sx={{ fontSize: 10, color: 'rgba(26,16,5,.6)', fontFamily: 'monospace' }}>
          {result.category} · {result.post_type}
          {result.revised ? ' · revised' : ''}
        </Typography>
      </Box>

      <Box
        sx={{
          p: 2.5,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
          gap: 2.5,
        }}
      >
        {sections.map(({ key, label, text }) => (
          <Box key={key} sx={{ borderLeft: '2px solid rgba(212,168,75,.45)', pl: 1.5 }}>
            <Typography
              sx={{
                fontSize: 8.5,
                fontWeight: 700,
                letterSpacing: '.12em',
                textTransform: 'uppercase',
                color: 'text.disabled',
                mb: 0.5,
              }}
            >
              {label}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.65 }}>
              {text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── GenerateFromBriefSection ──────────────────────────────────────────────────

function GenerateFromBriefSection({
  brief,
  category,
  postType,
}: {
  brief: Record<string, unknown>;
  category: string;
  postType: string;
}) {
  const generateImage = useGenerateImage();
  const generateReel = useGenerateReel();
  const generateCarousel = useGenerateCarousel();

  const [imageResult, setImageResult] = useState<MediaResponse | null>(null);
  const [reelJobId, setReelJobId] = useState<string | null>(null);
  const [carouselSlides, setCarouselSlides] = useState<MediaResponse[] | null>(null);
  const [genError, setGenError] = useState<string | null>(null);

  const reelJob = useReelJob(reelJobId);

  const visualDirection = (brief.visual_direction as string | undefined) ?? '';
  const imageVisualDirection =
    (brief.image_visual_direction as string | undefined) ?? visualDirection;
  const slidePrompts = brief.slide_prompts as Array<{ slide: number; prompt: string }> | undefined;

  const catForMedia = (
    ['whiskey', 'cigar', 'lifestyle'].includes(category) ? category : 'whiskey'
  ) as 'whiskey' | 'cigar' | 'lifestyle';

  const showImage = ['image', 'all'].includes(postType);
  const showReel = ['reel', 'video', 'all'].includes(postType);
  const showCarousel = postType === 'carousel' && !!slidePrompts && slidePrompts.length > 0;

  const reelStatus = reelJob.data?.status;
  const reelDone = reelStatus === 'done';
  const reelError = reelStatus === 'error';
  const reelRunning = reelJobId !== null && !reelDone && !reelError;

  const isGeneratingImage = generateImage.isPending;
  const isGeneratingReel = generateReel.isPending || reelRunning;
  const hasOutput = imageResult !== null || reelJobId !== null || carouselSlides !== null;

  async function handleGenerateImage() {
    setGenError(null);
    setImageResult(null);
    try {
      const res = await generateImage.mutateAsync({
        visual_direction: imageVisualDirection,
        aspect_ratio: postType === 'reel' ? '9:16' : '4:5',
        use_brand_reference: true,
        category: catForMedia,
      });
      setImageResult(res);
    } catch (e) {
      setGenError(e instanceof Error ? e.message.slice(0, 160) : 'Image generation failed');
    }
  }

  async function handleGenerateReel() {
    setGenError(null);
    setReelJobId(null);
    try {
      const res = await generateReel.mutateAsync({
        visual_direction: visualDirection,
        n_clips: 3,
        aspect_ratio: '9:16',
        with_audio: true,
        use_brand_reference: true,
        category: catForMedia,
      });
      setReelJobId(res.job_id);
    } catch (e) {
      setGenError(e instanceof Error ? e.message.slice(0, 160) : 'Reel generation failed');
    }
  }

  async function handleGenerateCarousel() {
    if (!slidePrompts) return;
    setGenError(null);
    setCarouselSlides(null);
    try {
      const res = await generateCarousel.mutateAsync({
        slide_prompts: slidePrompts.map((s) => s.prompt),
        aspect_ratio: '4:5',
        use_brand_reference: true,
        category: catForMedia,
      });
      setCarouselSlides(res.slides);
    } catch (e) {
      setGenError(e instanceof Error ? e.message.slice(0, 160) : 'Carousel generation failed');
    }
  }

  return (
    <Box
      sx={{
        mt: 2,
        border: `1.5px solid ${colors.border}`,
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        animation: 'brief-in .35s ease .1s both',
      }}
    >
      {/* Header + action buttons */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          bgcolor: '#f3f4f6',
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          Generate Content
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {showImage && (
            <Button
              size="small"
              variant="contained"
              disabled={isGeneratingImage || !imageVisualDirection}
              onClick={handleGenerateImage}
              sx={{
                bgcolor: colors.gold,
                color: '#1a1005',
                fontWeight: 700,
                fontSize: 11,
                boxShadow: '0 2px 8px rgba(212,168,75,.22)',
                '&:hover': { bgcolor: colors.goldDark },
                '&.Mui-disabled': { opacity: 0.45, bgcolor: colors.gold, color: '#1a1005' },
              }}
            >
              {isGeneratingImage && (
                <CircularProgress size={13} sx={{ color: '#1a1005', mr: 0.75 }} />
              )}
              {isGeneratingImage ? 'Generating…' : '↳ Generate Image'}
            </Button>
          )}
          {showReel && (
            <Button
              size="small"
              variant="outlined"
              disabled={isGeneratingReel || !visualDirection}
              onClick={handleGenerateReel}
              sx={{
                fontSize: 11,
                fontWeight: 700,
                borderColor: 'rgba(212,168,75,.55)',
                color: colors.goldDark,
                bgcolor: 'rgba(212,168,75,.09)',
                '&:hover': { bgcolor: 'rgba(212,168,75,.18)', borderColor: colors.gold },
              }}
            >
              {generateReel.isPending && (
                <CircularProgress size={13} sx={{ color: colors.goldDark, mr: 0.75 }} />
              )}
              {generateReel.isPending ? 'Queuing…' : '↳ Generate Reel'}
            </Button>
          )}
          {showCarousel && slidePrompts && (
            <Button
              size="small"
              variant="outlined"
              disabled={generateCarousel.isPending}
              onClick={handleGenerateCarousel}
              sx={{
                fontSize: 11,
                fontWeight: 700,
                borderColor: 'rgba(212,168,75,.55)',
                color: colors.goldDark,
                bgcolor: 'rgba(212,168,75,.09)',
                '&:hover': { bgcolor: 'rgba(212,168,75,.18)', borderColor: colors.gold },
              }}
            >
              {generateCarousel.isPending && (
                <CircularProgress size={13} sx={{ color: colors.goldDark, mr: 0.75 }} />
              )}
              {generateCarousel.isPending
                ? `Generating ${slidePrompts.length} slides…`
                : `↳ Generate Carousel (${slidePrompts.length} slides)`}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Empty state */}
      {!hasOutput && !genError && (
        <Box sx={{ px: 2.5, py: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', fontStyle: 'italic' }}>
            Click a button above to generate content from this brief
          </Typography>
        </Box>
      )}

      {/* Error */}
      {genError && (
        <Alert severity="error" sx={{ m: 2 }}>
          {genError}
        </Alert>
      )}

      {/* Reel: in-progress */}
      {reelRunning && (
        <Box sx={{ px: 2.5, py: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CircularProgress size={18} sx={{ color: colors.gold }} />
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                Rendering reel — {reelStatus ?? 'pending'}
              </Typography>
              {reelJob.data && (
                <Typography
                  sx={{ fontSize: 10, color: 'text.disabled', fontFamily: 'monospace', mt: 0.25 }}
                >
                  {reelJob.data.n_clips} clips · polling every 10 s
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      )}

      {/* Reel: error */}
      {reelError && (
        <Alert severity="error" sx={{ m: 2 }}>
          Reel error: {reelJob.data?.error ?? 'Unknown error'}
        </Alert>
      )}

      {/* Reel: done */}
      {reelDone && reelJob.data?.url && (
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box
              component="video"
              src={resolveMediaUrl(reelJob.data.url)}
              controls
              loop
              sx={{
                maxWidth: 320,
                maxHeight: 568,
                width: '100%',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                bgcolor: '#000',
              }}
            />
            <Stack spacing={1} sx={{ flex: 1, minWidth: 180 }}>
              <Typography
                sx={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  color: 'text.disabled',
                }}
              >
                Reel ready
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {reelJob.data.n_clips} clip{reelJob.data.n_clips !== 1 ? 's' : ''} ·{' '}
                {reelJob.data.beats.length} beat{reelJob.data.beats.length !== 1 ? 's' : ''}
              </Typography>
              <Button
                component="a"
                href={resolveMediaUrl(reelJob.data.url)}
                download
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                variant="outlined"
                sx={{ alignSelf: 'flex-start', fontSize: 11, borderColor: colors.border }}
              >
                ↓ Download
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      {/* Image result */}
      {imageResult && (
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box
              component="img"
              src={resolveMediaUrl(imageResult.url)}
              alt="Generated image"
              sx={{
                maxWidth: 320,
                maxHeight: 400,
                width: '100%',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                objectFit: 'cover',
              }}
            />
            <Stack spacing={1} sx={{ flex: 1, minWidth: 180 }}>
              <Typography
                sx={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  color: 'text.disabled',
                }}
              >
                Image ready
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {imageResult.provider} ·{' '}
                {imageResult.used_reference ? 'anchored to HY reference' : 'no reference'}
              </Typography>
              <Typography sx={{ fontSize: 10, fontFamily: 'monospace', color: 'text.disabled' }}>
                ~${imageResult.cost_usd.toFixed(4)}
              </Typography>
              <Button
                component="a"
                href={resolveMediaUrl(imageResult.url)}
                download
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                variant="outlined"
                sx={{ alignSelf: 'flex-start', fontSize: 11, borderColor: colors.border }}
              >
                ↓ Download
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      {/* Carousel result */}
      {carouselSlides && (
        <Box sx={{ p: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              overflowX: 'auto',
              pb: 1,
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(212,168,75,.4)', borderRadius: 2 },
            }}
          >
            {carouselSlides.map((slide, i) => (
              <Box key={i} sx={{ flexShrink: 0, position: 'relative' }}>
                <Box
                  component="img"
                  src={resolveMediaUrl(slide.url)}
                  alt={`Slide ${i + 1}`}
                  sx={{
                    width: 200,
                    height: 250,
                    borderRadius: '6px',
                    border: `1px solid ${colors.border}`,
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                <Typography
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    left: 4,
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#fff',
                    bgcolor: 'rgba(0,0,0,.55)',
                    px: 0.5,
                    borderRadius: '3px',
                  }}
                >
                  {i + 1}/{carouselSlides.length}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Blog heatmap cell (mirrors VisualParamCell style) ────────────────────────

// ── Industry News Panel ───────────────────────────────────────────────────────

function IndustryNewsPanel({
  selectedUrl,
  onSelect,
  onClear,
}: {
  selectedUrl: string | null;
  onSelect: (story: NewsStory) => void;
  onClear: () => void;
}) {
  const news = useIndustryNews();
  const [expanded, setExpanded] = useState(false);

  if (news.isLoading) {
    return (
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <CircularProgress size={10} sx={{ color: colors.gold }} />
          <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>Loading live news…</Typography>
        </Stack>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={44} sx={{ mb: 0.75, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  const digest = news.data;
  if (!digest || digest.error || digest.stories.length === 0) {
    return (
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          bgcolor: '#faf8f2',
          border: `1px dashed ${colors.border}`,
          borderRadius: 1.5,
        }}
      >
        <Typography sx={{ fontSize: 11, color: 'text.disabled', textAlign: 'center' }}>
          {digest?.error ? `News unavailable: ${digest.error}` : 'No stories in digest yet.'}
        </Typography>
      </Box>
    );
  }

  const visibleStories = expanded ? digest.stories : digest.stories.slice(0, 4);

  return (
    <Box sx={{ mb: 2 }}>
      {/* Takeaway banner */}
      {digest.headline_takeaway && (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            mb: 1.25,
            bgcolor: 'rgba(212,168,75,.07)',
            border: `1px solid rgba(212,168,75,.22)`,
            borderRadius: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: colors.gold,
              mb: 0.25,
            }}
          >
            This week
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.primary', lineHeight: 1.45 }}>
            {digest.headline_takeaway}
          </Typography>
        </Box>
      )}

      {/* Story cards */}
      <Typography
        sx={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: 'text.disabled',
          mb: 0.75,
        }}
      >
        Click a story to anchor the article
      </Typography>

      <Stack spacing={0.75}>
        {visibleStories.map((story) => {
          const isSelected = selectedUrl === story.url;
          return (
            <Box
              key={story.url}
              onClick={() => (isSelected ? onClear() : onSelect(story))}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                border: `1px solid ${isSelected ? colors.gold : colors.border}`,
                bgcolor: isSelected ? 'rgba(212,168,75,.09)' : '#fafaf8',
                cursor: 'pointer',
                transition: 'all 0.12s',
                '&:hover': {
                  borderColor: colors.gold,
                  bgcolor: 'rgba(212,168,75,.05)',
                },
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
                    <Typography
                      sx={{
                        fontSize: 8.5,
                        fontWeight: 700,
                        letterSpacing: '.08em',
                        textTransform: 'uppercase',
                        color: isSelected ? colors.goldDark : 'text.disabled',
                        flexShrink: 0,
                      }}
                    >
                      {story.source}
                    </Typography>
                    {isSelected && (
                      <Typography sx={{ fontSize: 8, color: colors.gold, fontWeight: 700 }}>
                        ✓ selected
                      </Typography>
                    )}
                  </Stack>
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? 'text.primary' : 'text.secondary',
                      lineHeight: 1.35,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {story.headline}
                  </Typography>
                  {story.why_it_matters && (
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        color: 'text.disabled',
                        mt: 0.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {story.why_it_matters}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          );
        })}
      </Stack>

      {digest.stories.length > 4 && (
        <Box
          onClick={() => setExpanded((v) => !v)}
          sx={{
            mt: 0.75,
            fontSize: 11,
            color: 'text.disabled',
            cursor: 'pointer',
            textAlign: 'center',
            '&:hover': { color: 'text.secondary' },
          }}
        >
          {expanded ? 'Show less' : `Show ${digest.stories.length - 4} more stories`}
        </Box>
      )}
    </Box>
  );
}

function BlogParamCell({
  value,
  isSelected,
  maxEng,
  onSelect,
}: {
  value: BlogParamValue;
  isSelected: boolean;
  maxEng: number;
  onSelect: () => void;
}) {
  const fillPct = maxEng > 0 ? (value.avg_eng / maxEng) * 100 : 0;
  const suggested = value.avg_eng === maxEng && maxEng > 0;
  return (
    <Box
      onClick={onSelect}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        borderRadius: 1,
        border: isSelected ? `1.5px solid ${colors.gold}` : `1px solid ${colors.border}`,
        bgcolor: isSelected ? 'rgba(212,168,75,.10)' : '#faf9f6',
        overflow: 'hidden',
        minHeight: 38,
        px: 1,
        py: 0.5,
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover': { borderColor: colors.gold, boxShadow: '0 0 0 2px rgba(212,168,75,.15)' },
      }}
    >
      {/* fill bar */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          width: `${fillPct}%`,
          bgcolor: isSelected ? 'rgba(212,168,75,.18)' : 'rgba(212,168,75,.08)',
          transition: 'width 0.3s ease',
          borderRadius: 1,
        }}
      />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ position: 'relative', zIndex: 1 }}
      >
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: isSelected ? 700 : 400,
            color: isSelected ? colors.goldDark : 'text.primary',
          }}
        >
          {suggested && <span style={{ marginRight: 3, color: colors.gold }}>★</span>}
          {value.label}
        </Typography>
        <Stack alignItems="flex-end">
          <Typography sx={{ fontSize: 10, fontWeight: 600, color: colors.goldDark }}>
            {value.avg_eng.toFixed(1)}
          </Typography>
          <Typography sx={{ fontSize: 9, color: 'text.disabled' }}>
            {value.pct.toFixed(0)}%
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

// ── Blog draft section (rendered when Format = Blog) ─────────────────────────

function BlogDraftSection() {
  const [mode, setMode] = useState<'freeform' | 'from_data'>('freeform');
  const [category, setCategory] = useState<'all' | 'whiskey' | 'cigar'>('all');
  const [event, setEvent] = useState('');
  const [selectedStoryUrl, setSelectedStoryUrl] = useState<string | null>(null);
  const [useNews, setUseNews] = useState(true);
  const [selections, setSelections] = useState<Record<string, SelectedBlogParam>>({});
  const [draft, setDraft] = useState<BlogDraft | GenerateFromBlogParamsResponse | null>(null);

  const generateBlog = useGenerateBlogDraft();
  const generateFromData = useGenerateFromBlogParams();
  const blogParams = useBlogParameters(category);

  const isPending = generateBlog.isPending || generateFromData.isPending;

  function toggleSelection(group: BlogParamGroup, field: BlogParamField, value: BlogParamValue) {
    const key = field.field_id;
    setSelections((prev) => {
      if (prev[key]?.value_id === value.id) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return {
        ...prev,
        [key]: {
          group_id: group.group_id,
          field_id: field.field_id,
          value_id: value.id,
          value_label: value.label,
        },
      };
    });
  }

  async function handleGenerate() {
    let result: BlogDraft | GenerateFromBlogParamsResponse;
    if (mode === 'from_data') {
      result = await generateFromData.mutateAsync({
        category,
        selected: Object.values(selections),
        event: event.trim() || undefined,
        use_news: useNews,
      });
    } else {
      result = await generateBlog.mutateAsync({
        topic: event.trim() || undefined,
        use_news: useNews,
      });
    }
    setDraft(result);
    if (!result.error) {
      setTimeout(() => {
        document
          .getElementById('blog-draft-output')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    }
  }

  return (
    <Box
      sx={{
        border: `1.5px solid ${colors.border}`,
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${colors.border}`, bgcolor: '#f9f8f4' }}>
        <Typography
          sx={{
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '.13em',
            textTransform: 'uppercase',
            color: colors.gold,
            mb: 0.25,
          }}
        >
          Toast & Light
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Palatino Linotype", Palatino, "Book Antiqua", Georgia, serif',
            fontSize: 18,
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          Blog Draft Generator
        </Typography>

        {/* Mode toggle */}
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          {(['freeform', 'from_data'] as const).map((m) => (
            <Box
              key={m}
              onClick={() => setMode(m)}
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                border: `1px solid ${mode === m ? colors.gold : colors.border}`,
                bgcolor: mode === m ? 'rgba(212,168,75,.1)' : 'transparent',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: mode === m ? 700 : 400,
                color: mode === m ? colors.goldDark : 'text.secondary',
                userSelect: 'none',
                transition: 'all 0.15s',
              }}
            >
              {m === 'freeform' ? 'Freeform' : 'Build from blog data'}
            </Box>
          ))}
        </Stack>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {/* ── From blog data mode: category + heatmap ── */}
        {mode === 'from_data' && (
          <Box sx={{ mb: 2 }}>
            {/* Category filter */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {(['all', 'whiskey', 'cigar'] as const).map((c) => (
                <Box
                  key={c}
                  onClick={() => setCategory(c)}
                  sx={{
                    px: 1.25,
                    py: '3px',
                    borderRadius: 1,
                    border: `1px solid ${category === c ? colors.gold : colors.border}`,
                    bgcolor: category === c ? 'rgba(212,168,75,.1)' : 'transparent',
                    cursor: 'pointer',
                    fontSize: 10.5,
                    fontWeight: category === c ? 700 : 400,
                    color: category === c ? colors.goldDark : 'text.secondary',
                    textTransform: 'capitalize',
                  }}
                >
                  {c === 'all' ? 'All sources' : c.charAt(0).toUpperCase() + c.slice(1)}
                </Box>
              ))}
              <Typography
                sx={{ fontSize: 10, color: 'text.disabled', alignSelf: 'center', ml: 'auto' }}
              >
                {blogParams.data?.total_analyzed ?? 0} articles ·{' '}
                {blogParams.data
                  ? `${category === 'whiskey' ? 7 : category === 'cigar' ? 6 : 13} sources`
                  : '—'}
              </Typography>
            </Stack>

            {blogParams.isLoading && (
              <Stack alignItems="center" py={3}>
                <CircularProgress size={24} sx={{ color: colors.gold }} />
              </Stack>
            )}

            {blogParams.data && blogParams.data.total_analyzed === 0 && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#faf8f2',
                  border: `1px dashed ${colors.border}`,
                  borderRadius: 1.5,
                  mb: 2,
                }}
              >
                <Typography sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center' }}>
                  No blog data yet — run{' '}
                  <code>uv run python scripts/ingest_competitor_blogs.py</code> to populate.
                </Typography>
              </Box>
            )}

            {blogParams.data &&
              blogParams.data.total_analyzed > 0 &&
              blogParams.data.groups.map((group: BlogParamGroup) => (
                <Box key={group.group_id} sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: '.1em',
                      textTransform: 'uppercase',
                      color: colors.gold,
                      mb: 1,
                    }}
                  >
                    {group.group_label}
                  </Typography>
                  {group.fields.map((field: BlogParamField) => {
                    const maxEng = Math.max(
                      ...field.values.map((v: BlogParamValue) => v.avg_eng),
                      0,
                    );
                    return (
                      <Box key={field.field_id} sx={{ mb: 1.5 }}>
                        <Typography
                          sx={{ fontSize: 10.5, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}
                        >
                          {field.field_label}
                          {!field.available && (
                            <span style={{ fontSize: 9, color: '#bbb', marginLeft: 6 }}>
                              no data yet
                            </span>
                          )}
                        </Typography>
                        <Stack spacing={0.5}>
                          {field.values.map((value: BlogParamValue) => (
                            <BlogParamCell
                              key={value.id}
                              value={value}
                              isSelected={selections[field.field_id]?.value_id === value.id}
                              maxEng={maxEng}
                              onSelect={() => toggleSelection(group, field, value)}
                            />
                          ))}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              ))}

            {Object.keys(selections).length > 0 && (
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                <Box
                  onClick={() => setSelections({})}
                  sx={{
                    fontSize: 11,
                    color: 'text.disabled',
                    cursor: 'pointer',
                    '&:hover': { color: 'text.secondary' },
                  }}
                >
                  Clear selections
                </Box>
              </Stack>
            )}
          </Box>
        )}

        {/* Current Events section */}
        <Box sx={{ mb: 0.5 }}>
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: colors.gold,
              mb: 1,
            }}
          >
            Current Events
          </Typography>

          <IndustryNewsPanel
            selectedUrl={selectedStoryUrl}
            onSelect={(story) => {
              setSelectedStoryUrl(story.url);
              setEvent(story.headline);
            }}
            onClear={() => {
              setSelectedStoryUrl(null);
              setEvent('');
            }}
          />

          {/* Manual override / edit of the anchor */}
          <TextField
            fullWidth
            size="small"
            label="Event anchor (edit or type your own)"
            placeholder="e.g. 250th Independence Day, Masters weekend, barrel char debate"
            value={event}
            onChange={(e) => {
              setEvent(e.target.value);
              if (!e.target.value.trim()) setSelectedStoryUrl(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isPending) handleGenerate();
            }}
            sx={{ mb: 1.5 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={useNews}
                onChange={(e) => setUseNews(e.target.checked)}
                size="small"
                sx={{ color: colors.gold, '&.Mui-checked': { color: colors.gold } }}
              />
            }
            label={
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Also weave in full spirits-industry news digest
              </Typography>
            }
            sx={{ mb: 2 }}
          />
        </Box>

        <Button
          variant="contained"
          fullWidth
          disabled={isPending || (mode === 'from_data' && Object.keys(selections).length === 0)}
          onClick={handleGenerate}
          sx={{
            bgcolor: colors.gold,
            color: '#1a1005',
            fontWeight: 700,
            boxShadow: '0 4px 14px rgba(212,168,75,.28)',
            '&:hover': { bgcolor: colors.goldDark, boxShadow: '0 6px 18px rgba(212,168,75,.38)' },
            '&.Mui-disabled': { opacity: 0.42, bgcolor: colors.gold, color: '#1a1005' },
          }}
        >
          {isPending ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={16} sx={{ color: '#1a1005' }} />
              <span>Drafting article...</span>
            </Stack>
          ) : mode === 'from_data' ? (
            `Generate from ${Object.keys(selections).length} selected parameter${Object.keys(selections).length === 1 ? '' : 's'} →`
          ) : (
            'Generate Blog Draft →'
          )}
        </Button>

        {/* Draft output */}
        {draft && (
          <Box id="blog-draft-output" sx={{ mt: 3 }}>
            {draft.error ? (
              <Alert severity="error">{draft.error}</Alert>
            ) : (
              <>
                <Divider sx={{ mb: 2 }} />

                {/* Featured brand image */}
                {draft.featured_image_url && (
                  <Box
                    sx={{
                      mb: 2,
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      border: `1px solid ${colors.border}`,
                      maxHeight: 280,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#f0ece4',
                    }}
                  >
                    <Box
                      component="img"
                      src={resolveMediaUrl(draft.featured_image_url)}
                      alt={draft.title}
                      sx={{ width: '100%', maxHeight: 280, objectFit: 'cover' }}
                    />
                  </Box>
                )}

                {/* Title + excerpt */}
                <Typography
                  sx={{
                    fontFamily: '"Palatino Linotype", Palatino, "Book Antiqua", Georgia, serif',
                    fontSize: 20,
                    fontWeight: 700,
                    color: 'text.primary',
                    lineHeight: 1.25,
                    mb: 0.75,
                  }}
                >
                  {draft.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    mb: 2,
                    lineHeight: 1.6,
                  }}
                >
                  {draft.excerpt}
                </Typography>

                {/* Body */}
                <Box
                  sx={{
                    p: 2,
                    bgcolor: '#f9f8f5',
                    borderRadius: 1.5,
                    border: `1px solid ${colors.border}`,
                    maxHeight: 420,
                    overflowY: 'auto',
                    mb: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      lineHeight: 1.75,
                      color: 'text.primary',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {draft.body_markdown}
                  </Typography>
                </Box>

                {/* Photo brief for photographer — shoot direction for the featured image */}
                {draft.featured_image_concept && (
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'rgba(212,168,75,.07)',
                      border: `1px solid rgba(212,168,75,.3)`,
                      borderRadius: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: '.1em',
                        textTransform: 'uppercase',
                        color: colors.goldDark,
                        mb: 0.5,
                      }}
                    >
                      Photo brief for featured image
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.6 }}>
                      {draft.featured_image_concept}
                    </Typography>
                  </Box>
                )}

                {/* Tags */}
                {draft.tags.length > 0 && (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {draft.tags.map((tag) => (
                      <Box
                        key={tag}
                        sx={{
                          px: 1,
                          py: '3px',
                          bgcolor: 'rgba(212,168,75,.12)',
                          border: `1px solid rgba(212,168,75,.3)`,
                          borderRadius: '4px',
                          fontSize: 10.5,
                          fontWeight: 600,
                          color: colors.goldDark,
                        }}
                      >
                        #{tag}
                      </Box>
                    ))}
                  </Stack>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function MarketingPatternBuilderPage() {
  const [category, setCategory] = useState('whiskey');
  const [postType, setPostType] = useState('reel');
  const [selections, setSelections] = useState<SelectionMap>({});
  const [mounted, setMounted] = useState(false);
  const [briefResult, setBriefResult] = useState<GenerateFromParamsResponse | null>(null);
  const [snack, setSnack] = useState<string | null>(null);

  const { data, isLoading, error } = useVisualParameters(category, postType);
  const generate = useGenerateFromParams();

  // Trigger fill animation after data lands (and re-trigger on category/format change)
  useEffect(() => {
    setMounted(false);
    const t = setTimeout(() => setMounted(true), 70);
    return () => clearTimeout(t);
  }, [data]);

  function toggleSelection(entry: SelectionEntry) {
    const key = `${entry.group_id}.${entry.field_id}`;
    setSelections((prev) => {
      if (prev[key]?.value_id === entry.value_id) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: entry };
    });
  }

  function clearAll() {
    setSelections({});
    setBriefResult(null);
  }

  async function handleGenerate() {
    const selected: SelectedParam[] = Object.values(selections).map(
      ({ group_id, field_id, value_id, value_label }) => ({
        group_id,
        field_id,
        value_id,
        value_label,
      }),
    );
    const req: GenerateFromParamsRequest = { category, post_type: postType, selected };
    try {
      const result = await generate.mutateAsync(req);
      setBriefResult(result);
      setTimeout(() => {
        document.getElementById('pb-brief')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    } catch {
      setSnack('Generation failed — check API connection');
    }
  }

  const selVals = Object.values(selections);
  const selCount = selVals.length;
  const score = selCount > 0 ? Math.round(selVals.reduce((s, v) => s + v.pct, 0) / selCount) : null;

  const sortedSelections = selVals
    .slice()
    .sort((a, b) => GROUP_ORDER.indexOf(a.group_id) - GROUP_ORDER.indexOf(b.group_id));

  return (
    <SiteLayout subtitle="Marketing Intelligence" showDesktopHint>
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
        {/* Back */}
        <Box sx={{ mb: 2.5 }}>
          <RouterLink
            to="/marketing"
            style={{
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: colors.textMuted,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            Back to Marketing
          </RouterLink>
        </Box>

        {/* Header + filters — premium banner */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            mb: 2.5,
            px: { xs: 2.5, md: 4 },
            py: { xs: 2.75, md: 3.25 },
            border: '1px solid rgba(212,168,75,.28)',
            background: 'linear-gradient(135deg, #fbf7ef 0%, #f7efe0 46%, #f3e6cf 100%)',
            boxShadow: '0 8px 30px -12px rgba(187,140,63,.35)',
          }}
        >
          {/* Soft gold glow, top-right */}
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              top: -70,
              right: -50,
              width: 260,
              height: 260,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(244,188,88,.30) 0%, rgba(244,188,88,0) 70%)',
              pointerEvents: 'none',
            }}
          />
          {/* Fine hairline accent along the top edge */}
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: `linear-gradient(90deg, ${colors.goldLight}, ${colors.gold}, ${colors.goldDark})`,
            }}
          />
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ md: 'flex-end' }}
            spacing={2}
            sx={{ position: 'relative' }}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: colors.gold,
                    boxShadow: `0 0 0 3px rgba(212,168,75,.2)`,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '.16em',
                    textTransform: 'uppercase',
                    color: colors.goldDark,
                  }}
                >
                  Pattern Builder
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontFamily: '"Playfair Display", "Palatino Linotype", Palatino, Georgia, serif',
                  fontSize: { xs: 26, md: 34 },
                  fontWeight: 700,
                  color: colors.brandDark,
                  lineHeight: 1.08,
                  letterSpacing: '-.01em',
                  mb: 0.6,
                }}
              >
                Build from visual data
              </Typography>
              <Typography
                sx={{
                  fontSize: 12.5,
                  color: 'text.secondary',
                  maxWidth: 540,
                  lineHeight: 1.65,
                }}
              >
                Fill height = how often each parameter appears in top posts. Brightness = engagement
                lift. Select one value per row — the brief composes from what actually works.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexShrink={0} flexWrap="wrap" alignItems="center">
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  label="Category"
                  onChange={(e) => {
                    setCategory(e.target.value);
                    clearAll();
                  }}
                >
                  <MenuItem value="whiskey">Whiskey</MenuItem>
                  <MenuItem value="cigar">Cigar</MenuItem>
                  {FEATURE_LIFESTYLE_CATEGORY ? (
                    <MenuItem value="lifestyle">Lifestyle</MenuItem>
                  ) : null}
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel>Format</InputLabel>
                <Select
                  value={postType}
                  label="Format"
                  onChange={(e) => {
                    setPostType(e.target.value);
                    clearAll();
                  }}
                >
                  <MenuItem value="reel">Reel</MenuItem>
                  <MenuItem value="image">Image</MenuItem>
                  <MenuItem value="carousel">Carousel</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="blog">Blog</MenuItem>
                </Select>
              </FormControl>
              {postType !== 'blog' && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!data}
                  onClick={() => data && setSelections(buildSuggestedPattern(data.groups))}
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    borderColor: 'rgba(212,168,75,.55)',
                    color: colors.goldDark,
                    bgcolor: 'rgba(212,168,75,.09)',
                    '&:hover': { bgcolor: 'rgba(212,168,75,.18)', borderColor: colors.gold },
                  }}
                >
                  ★ Suggest best
                </Button>
              )}
              {selCount > 0 && postType !== 'blog' && (
                <Button size="small" variant="outlined" onClick={clearAll} sx={{ fontSize: 11 }}>
                  Clear ({selCount})
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>

        {/* Legend — only for heatmap formats */}
        {postType !== 'blog' && (
          <Stack direction="row" spacing={2.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
            {[
              {
                swatch: { bgcolor: 'rgba(212,168,75,.38)', border: `1px solid ${colors.border}` },
                label: 'Fill height = post frequency',
              },
              {
                swatch: {
                  bgcolor: 'rgba(212,168,75,.08)',
                  border: '1.5px dashed rgba(212,168,75,.55)',
                },
                label: '★ Highest engagement lift',
              },
              {
                swatch: { bgcolor: 'rgba(212,168,75,.24)', border: `2px solid ${colors.gold}` },
                label: 'Selected',
              },
            ].map(({ swatch, label }) => (
              <Stack key={label} direction="row" alignItems="center" spacing={0.75}>
                <Box
                  sx={{ width: 22, height: 14, borderRadius: '3px', flexShrink: 0, ...swatch }}
                />
                <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{label}</Typography>
              </Stack>
            ))}

            {/* Category color key */}
            <Box sx={{ width: '1px', height: 14, bgcolor: 'divider', mx: 0.5 }} />
            <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap">
              <Typography sx={{ fontSize: 10, color: 'text.disabled', fontWeight: 600 }}>
                Color = category:
              </Typography>
              {(
                [
                  ['human', 'Human'],
                  ['framing', 'Framing'],
                  ['environment', 'Environment'],
                  ['color', 'Color'],
                  ['product', 'Product'],
                ] as const
              ).map(([id, label]) => (
                <Stack key={id} direction="row" alignItems="center" spacing={0.5}>
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      bgcolor: groupPalette(id).hue,
                      flexShrink: 0,
                    }}
                  />
                  <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{label}</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        )}

        {error && postType !== 'blog' && (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            Could not load visual parameters — make sure{' '}
            <code>scripts/run_content_analysis.py</code> has been run.
          </Alert>
        )}

        {/* Blog mode */}
        {postType === 'blog' && <BlogDraftSection />}

        {/* Main 2-column layout — heatmap formats only */}
        {postType !== 'blog' && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 296px' },
              gap: 2.5,
              alignItems: 'start',
            }}
          >
            {/* ── Heatmap ── */}
            <Box
              sx={{
                border: `1px solid ${colors.border}`,
                borderRadius: 3,
                bgcolor: 'background.paper',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,.04), 0 8px 24px -16px rgba(0,0,0,.12)',
              }}
            >
              {isLoading && (
                <Box sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} variant="rounded" height={72} />
                    ))}
                  </Stack>
                </Box>
              )}

              {data &&
                data.groups.map((group) => {
                  const gp = groupPalette(group.group_id);
                  return (
                    <Box
                      key={group.group_id}
                      sx={{
                        borderBottom: `1.5px solid ${colors.border}`,
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      {/* Group header — Depletions-style: 3px rail + numbered
                          step circle + whisper tint, color kept to the accents */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 2,
                          py: '10px',
                          borderLeft: `3px solid ${gp.hue}`,
                          bgcolor: `rgba(${gp.rgb},.035)`,
                          borderBottom: `1px solid ${colors.border}`,
                        }}
                      >
                        <Box
                          aria-hidden="true"
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            bgcolor: gp.hue,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Typography sx={{ fontSize: 9, fontWeight: 800, color: '#fff' }}>
                            {GROUP_ORDER.indexOf(group.group_id) + 1 || '·'}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 9.5,
                            fontWeight: 700,
                            letterSpacing: '.08em',
                            textTransform: 'uppercase',
                            color: gp.hue,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {group.group_label}
                        </Typography>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                        <Typography sx={{ fontSize: 9, fontFamily: 'monospace', color: '#9ca3af' }}>
                          {group.fields.length} field{group.fields.length > 1 ? 's' : ''}
                        </Typography>
                      </Box>

                      {/* Field rows */}
                      {group.fields.map((field) => (
                        <HeatRow
                          key={field.field_id}
                          group={group}
                          field={field}
                          selections={selections}
                          mounted={mounted}
                          onToggle={toggleSelection}
                        />
                      ))}
                    </Box>
                  );
                })}
            </Box>

            {/* ── Sidebar ── */}
            <Stack spacing={1.75} sx={{ position: { lg: 'sticky' }, top: { lg: 20 } }}>
              {/* Barrel gauge */}
              <Box
                sx={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,.04), 0 8px 24px -16px rgba(0,0,0,.12)',
                }}
              >
                <BarrelGauge score={score} selCount={selCount} />
              </Box>

              {/* Selections list */}
              <Box
                sx={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  p: 2,
                  boxShadow: '0 1px 3px rgba(0,0,0,.04), 0 8px 24px -16px rgba(0,0,0,.12)',
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1.25 }}
                >
                  <Typography
                    sx={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      letterSpacing: '.1em',
                      textTransform: 'uppercase',
                      color: 'text.disabled',
                    }}
                  >
                    Selected parameters
                  </Typography>
                  <Button
                    size="small"
                    onClick={clearAll}
                    disabled={selCount === 0}
                    sx={{ fontSize: 10, color: 'text.disabled', minWidth: 0, p: '2px 6px' }}
                  >
                    Clear all
                  </Button>
                </Stack>

                {selCount === 0 ? (
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: 'text.disabled',
                      fontStyle: 'italic',
                      py: 1,
                      textAlign: 'center',
                    }}
                  >
                    Pick values from the heatmap
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {sortedSelections.map((sel) => {
                      const gp = groupPalette(sel.group_id);
                      return (
                        <Box
                          key={`${sel.group_id}.${sel.field_id}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: `rgba(${gp.rgb},.09)`,
                            border: `1px solid rgba(${gp.rgb},.3)`,
                            borderLeft: `3px solid ${gp.hue}`,
                            borderRadius: '5px',
                            px: 1,
                            py: '4px',
                            animation: 'chip-in .18s ease',
                            '@keyframes chip-in': {
                              from: { opacity: 0, transform: 'translateY(-3px)' },
                              to: { opacity: 1, transform: 'translateY(0)' },
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 8.5,
                              fontWeight: 700,
                              color: gp.hueDark,
                              textTransform: 'uppercase',
                              letterSpacing: '.05em',
                              mr: 0.75,
                              flexShrink: 0,
                            }}
                          >
                            {sel.group_id.slice(0, 3)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 10.5,
                              fontWeight: 500,
                              color: 'text.secondary',
                              flex: 1,
                            }}
                          >
                            {sel.fieldLabel}: {sel.value_label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 9,
                              fontFamily: 'monospace',
                              color: gp.hueDark,
                              mr: 0.5,
                            }}
                          >
                            {sel.pct}%
                          </Typography>
                          <Box
                            component="button"
                            onClick={() => toggleSelection(sel)}
                            aria-label={`Remove ${sel.fieldLabel}`}
                            sx={{
                              background: 'none',
                              border: 'none',
                              p: '0 2px',
                              fontSize: 15,
                              color: 'text.disabled',
                              cursor: 'pointer',
                              lineHeight: 1,
                              '&:hover': { color: 'text.primary' },
                            }}
                          >
                            ×
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>

              {/* Generate */}
              <Box
                sx={{
                  border: '1px solid rgba(212,168,75,.35)',
                  borderRadius: 3,
                  background: 'linear-gradient(160deg, #fbf7ef 0%, #f6ecd8 100%)',
                  p: 2,
                  boxShadow: '0 1px 3px rgba(0,0,0,.04), 0 10px 28px -18px rgba(187,140,63,.5)',
                }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  disabled={selCount === 0 || generate.isPending}
                  onClick={handleGenerate}
                  sx={{
                    py: 1.15,
                    fontSize: 14,
                    letterSpacing: '.01em',
                    color: '#1a1005',
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${colors.goldLight} 0%, ${colors.gold} 55%, ${colors.goldDark} 100%)`,
                    boxShadow: '0 4px 14px rgba(212,168,75,.35)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'box-shadow .2s ease, transform .2s ease',
                    // Sheen sweep on hover.
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-60%',
                      width: '40%',
                      height: '100%',
                      background:
                        'linear-gradient(100deg, transparent, rgba(255,255,255,.55), transparent)',
                      transform: 'skewX(-18deg)',
                      transition: 'left .55s ease',
                    },
                    '&:hover': {
                      background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.goldDark} 100%)`,
                      boxShadow: '0 7px 22px rgba(212,168,75,.48)',
                      transform: 'translateY(-1px)',
                    },
                    '&:hover::after': { left: '120%' },
                    '&.Mui-disabled': {
                      opacity: 0.4,
                      background: `linear-gradient(135deg, ${colors.goldLight}, ${colors.gold})`,
                      color: '#1a1005',
                    },
                  }}
                >
                  {generate.isPending ? (
                    <CircularProgress size={18} sx={{ color: '#1a1005' }} />
                  ) : (
                    'Generate Brief →'
                  )}
                </Button>
                <Typography
                  sx={{ fontSize: 10, color: 'text.disabled', textAlign: 'center', mt: 1 }}
                >
                  {selCount === 0
                    ? 'Select parameters to generate'
                    : `${selCount} dimension${selCount > 1 ? 's' : ''} selected — ready`}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Brief result + content generation — heatmap formats only */}
        {briefResult && postType !== 'blog' && (
          <Box id="pb-brief">
            <BriefPanel result={briefResult} />
            <GenerateFromBriefSection
              brief={briefResult.brief}
              category={briefResult.category}
              postType={briefResult.post_type}
            />
          </Box>
        )}
      </Container>

      <Snackbar
        open={snack !== null}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </SiteLayout>
  );
}
